import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ensureDiscoverySession } from '../supabase';
import { createDiscoveryProject, updateDiscoveryProject, deleteDiscoveryProject } from '../discoveryProjectService';
import type { QueryState } from '../digestMachine';
import { buildProjectLimitMessage, isProjectLimitReachedError, toProjectLimitReachedError } from '../entitlements';
import { CURRENT_ENTITLEMENTS_QUERY_KEY } from './useCurrentEntitlements';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from '../i18n/messages';

/**
 * Handles project create, update, and delete operations
 */
export function useProjectMutations() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<LocalizedMessage | null>(null);
    const queryClient = useQueryClient();

    const createProject = async (data: QueryState): Promise<string> => {
        setIsLoading(true);
        setError(null);

        try {
            // Ensure we have a session (create anonymous if needed)
            const { session } = await ensureDiscoverySession();

            if (!session) {
                throw new Error('Failed to establish session. Cannot create project without authentication.');
            }

            console.log('🎯 [useProjectMutations] Creating project with query:', {
                categoriesCount: data.categories.length,
                interestTextLength: data.interestText.length,
                includeCount: data.include.length,
                excludeCount: data.exclude.length
            });

            const { projectId } = await createDiscoveryProject({
                interestText: data.interestText,
                categories: data.categories,
                includeKeywords: data.include,
                excludeKeywords: data.exclude
            });

            console.log('✅ [useProjectMutations] Project created:', projectId);
            void queryClient.invalidateQueries({ queryKey: CURRENT_ENTITLEMENTS_QUERY_KEY });
            return projectId;

        } catch (err: any) {
            console.error('❌ [useProjectMutations] Failed to create project:', err);

            // Provide user-friendly error messages
            let message: LocalizedMessage = msg({ id: 'projectMutation.createFailure', message: 'Failed to create project' });
            if (isProjectLimitReachedError(err)) {
                message = buildProjectLimitMessage(toProjectLimitReachedError(err).snapshot);
            } else if (err?.message?.includes('permission denied')) {
                message = msg({ id: 'projectMutation.createPermission', message: 'Permission error: Unable to create project. Please try refreshing the page.' });
            } else if (err?.message?.includes('network')) {
                message = msg({ id: 'projectMutation.network', message: 'Network error: Please check your connection and try again.' });
            }

            setError(message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateProject = async (projectId: string, data: QueryState): Promise<void> => {
        setIsLoading(true);
        setError(null);

        try {
            // Ensure we have a session
            const { session } = await ensureDiscoverySession();

            if (!session) {
                throw new Error('Failed to establish session. Cannot update project without authentication.');
            }

            console.log('📝 [useProjectMutations] Updating project:', projectId);

            await updateDiscoveryProject({
                projectId,
                interestText: data.interestText,
                categories: data.categories,
                includeKeywords: data.include,
                excludeKeywords: data.exclude
            });

            console.log('✅ [useProjectMutations] Project updated:', projectId);

        } catch (err: any) {
            console.error('❌ [useProjectMutations] Failed to update project:', err);

            let message: LocalizedMessage = msg({ id: 'projectMutation.updateFailure', message: 'Failed to update project' });
            if (err?.message?.includes('permission denied')) {
                message = msg({ id: 'projectMutation.updatePermission', message: 'Permission error: Unable to update project.' });
            } else if (err?.message?.includes('network')) {
                message = msg({ id: 'projectMutation.network', message: 'Network error: Please check your connection and try again.' });
            }

            setError(message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const deleteProject = async (projectId: string): Promise<void> => {
        setIsLoading(true);
        setError(null);

        try {
            // Ensure we have a session
            const { session } = await ensureDiscoverySession();

            if (!session) {
                throw new Error('Failed to establish session. Cannot delete project without authentication.');
            }

            console.log('🗑️ [useProjectMutations] Deleting project:', projectId);

            await deleteDiscoveryProject(projectId);

            console.log('✅ [useProjectMutations] Project deleted:', projectId);
            void queryClient.invalidateQueries({ queryKey: CURRENT_ENTITLEMENTS_QUERY_KEY });

        } catch (err: any) {
            console.error('❌ [useProjectMutations] Failed to delete project:', err);

            let message: LocalizedMessage = msg({ id: 'projectMutation.deleteFailure', message: 'Failed to delete project' });
            if (err?.message?.includes('permission denied')) {
                message = msg({ id: 'projectMutation.deletePermission', message: 'Permission error: Unable to delete project.' });
            } else if (err?.message?.includes('network')) {
                message = msg({ id: 'projectMutation.network', message: 'Network error: Please check your connection and try again.' });
            }

            setError(message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        createProject,
        updateProject,
        deleteProject,
        isLoading,
        error,
        clearError: () => setError(null)
    };
}
